import MobileAppBar from '@/components/molecules/MobileAppBar';
import InfoModal from '@/components/molecules/InfoModal';
import ProfileCreationForm from '@/components/organisms/ProfileCreationForm';
import profileDiscardIcon from '@/assets/icons/profile-discard.svg';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';
import { PAGE } from '@/routes';
import { userService } from '@/services/user.service';
import { getCurrentUser, setUser } from '@/store/slices/auth';
import { User } from '@/store/slices/auth/types';
import { setBreadcrumbs } from '@/store/slices/breadcrumb';
import { useUser } from '@clerk/clerk-react';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';

const EditProfilePage = () => {
  const currentUser = useSelector(getCurrentUser);
  const dispatch = useDispatch();
  const { user: clerkUser } = useUser();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [profile, setProfile] = useState<Partial<User> | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const { isDiscardModalOpen, handleCancel, handleDiscardChanges, handleDiscardModalClose, allowNavigation } =
    useNavigationGuard({
      isDirty: isFormDirty,
      fallbackPath: '/profile'
    });

  useEffect(() => {
    dispatch(
      setBreadcrumbs([
        {
          title: 'Profile',
          path: PAGE.PROFILE.path
        },
        {
          title: 'Edit Profile',
          path: ''
        }
      ])
    );
  }, [dispatch]);

  useEffect(() => {
    if (!clerkUser?.id) {
      setLoadingProfile(false);
      return;
    }

    setLoadingProfile(true);
    userService
      .getUserProfileByClerkId(clerkUser.id)
      .then(({ data }) => {
        const mappedProfile: Partial<User> = {
          firstName: data.firstName,
          lastName: data.lastName,
          username: data.username,
          email: data.email,
          phoneNumber: data.phoneNumber,
          photoURL: data.profilePicUrl ?? undefined,
          hourlyRate: data.hourlyRate ?? 0
        };

        setProfile(mappedProfile);

        dispatch(
          setUser({
            ...currentUser,
            firstName: data.firstName,
            lastName: data.lastName,
            name: `${data.firstName} ${data.lastName || ''}`.trim(),
            email: data.email,
            phoneNumber: data.phoneNumber,
            photoURL: data.profilePicUrl ?? undefined,
            id: data._id,
            fid: data.firebaseUid,
            username: data.username,
            hourlyRate: data.hourlyRate ?? 0,
            roles: data.roles ?? currentUser?.roles ?? [],
            status: data.status ?? currentUser?.status ?? 'ACTIVE',
            unregisterDate: data.unregisterDate ?? currentUser?.unregisterDate ?? null
          })
        );
      })
      .catch((error) => {
        console.error('Error fetching profile:', error);
        toast.error('Failed to load profile');
        setProfile(currentUser ?? {});
      })
      .finally(() => {
        setLoadingProfile(false);
      });
  }, [clerkUser?.id, dispatch]);

  if (loadingProfile) {
    return (
      <div className="pb-5 flex flex-col items-center justify-center gap-2 min-h-[200px]">
        <MobileAppBar title="Edit Profile" />
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="text-sm font-medium">Loading profile...</span>
      </div>
    );
  }

  return (
    <div className="pb-5">
      <MobileAppBar title="Edit Profile" />
      <ProfileCreationForm
        variant="edit"
        size="sm"
        profile={profile ?? currentUser}
        onFormStateChange={({ isDirty }) => setIsFormDirty(isDirty)}
        onCancel={handleCancel}
        onBeforeSuccessfulNavigate={allowNavigation}
      />

      {isDiscardModalOpen && (
        <InfoModal
          isOpen={isDiscardModalOpen}
          variant="confirm"
          title="Are you sure you want to discard changes?"
          message="Unsaved changes will not be saved into your profile"
          buttonText="Discard"
          onButtonClick={handleDiscardChanges}
          icon={profileDiscardIcon}
          onClose={handleDiscardModalClose}
        />
      )}
    </div>
  );
};

export default EditProfilePage;
